import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { useI18n, LANGUAGES } from "@/lib/i18n";
import { isWebUsbSupported, printEscposBytes } from "@/lib/escposPrint";
import { openTicketPrint } from "@/lib/printTicket";
import { warnDev } from "@/lib/log";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { OfflineBanner, NetworkIndicator } from "@/components/NetworkIndicator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, ArrowRight, Clock, CheckCircle2, Printer, Globe, Smartphone, Users, RefreshCw, X, Maximize2, Minimize2 } from "lucide-react";

export default function KioskPage() {
  const { agencyId } = useParams();
  const nav = useNavigate();
  const { lang, setLang, t } = useI18n();
  const { online, backendReachable } = useNetworkStatus();
  const isOffline = !online || !backendReachable;
  const [agencies, setAgencies] = useState([]);
  const [agency, setAgency] = useState(null);
  const [categories, setCategories] = useState([]);
  const [phoneStep, setPhoneStep] = useState(null); // { category }
  const [phone, setPhone] = useState("");
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch (e) {
      warnDev("fullscreen toggle failed", e);
    }
  };

  // Idle auto-reset (30s on any open modal -> kiosk reset)
  useEffect(() => {
    if (!issued && !phoneStep) return;
    const reset = () => { setIssued(null); setPhoneStep(null); setPhone(""); };
    let timer = setTimeout(reset, 60000);
    const refresh = () => { clearTimeout(timer); timer = setTimeout(reset, 60000); };
    const events = ["touchstart", "click", "keydown"];
    events.forEach((e) => window.addEventListener(e, refresh));
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, refresh)); };
  }, [issued, phoneStep]);

  useEffect(() => {
    api.get("/agencies/public")
      .then((r) => setAgencies(r.data))
      .catch((e) => warnDev("kiosk agencies fetch failed", e));
  }, []);

  useEffect(() => {
    if (!agencyId) return;
    const a = agencies.find((x) => x.id === agencyId);
    setAgency(a || null);
    api.get("/categories", { params: { agency_id: agencyId } })
      .then((r) => setCategories(r.data))
      .catch((e) => { warnDev("kiosk categories fetch failed", e); setCategories([]); });
  }, [agencyId, agencies]);

  // Note: modal closes via 60s idle-reset effect below

  const askPhone = (cat) => {
    setPhoneStep({ category: cat });
    setPhone("");
  };

  const submitTicket = async (withPhone) => {
    if (busy || !agency || !phoneStep) return;
    if (isOffline) {
      toast.error("Connexion serveur indisponible. Veuillez réessayer dans quelques instants.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        agency_id: agency.id,
        category_code: phoneStep.category.code,
      };
      if (withPhone && phone.trim()) body.customer_phone_optional = phone.trim();
      const { data } = await api.post("/tickets", body);
      setIssued(data);
      setPhoneStep(null);
      setPhone("");

      // Auto-attempt network printing if agency configured
      if (agency.printer_mode === "network") {
        api.post(`/tickets/${data.id}/print-network`).catch((e) =>
          warnDev("network print failed", e?.response?.data)
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // Agency selection
  if (!agencyId) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <KioskHeader lang={lang} setLang={setLang} t={t} now={now}
                     isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen} />
        <main className="flex-1 flex flex-col items-center justify-center p-8">
          <h1 className="font-display font-bold text-4xl text-slate-900 mb-3">{t("kiosk.select_agency")}</h1>
          <p className="text-slate-500 mb-12">{t("kiosk.select_agency_hint")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl">
            {agencies.map((a) => (
              <button
                key={a.id}
                data-testid={`kiosk-agency-${a.code}`}
                onClick={() => nav(`/kiosk/${a.id}`)}
                className="kiosk-card group bg-white border-2 border-slate-200 rounded-2xl p-8 flex flex-col items-start gap-4 hover:shadow-lg"
              >
                <div className="w-14 h-14 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#DC2626]" />
                </div>
                <div className="text-left">
                  <div className="font-display font-bold text-2xl text-slate-900">{a.name}</div>
                  <div className="text-slate-500 text-sm mt-1">{a.city} • {a.opening_hours}</div>
                </div>
                <div className="mt-auto flex items-center gap-2 text-[#DC2626] font-semibold opacity-0 group-hover:opacity-100 transition">
                  Choisir <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <KioskHeader agency={agency} onSwitch={() => nav("/kiosk")} lang={lang} setLang={setLang} t={t}
                   now={now} isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen}
                   online={online} backendReachable={backendReachable} />
      <OfflineBanner online={online} backendReachable={backendReachable} />
      <main className="flex-1 p-6 md:p-10 flex flex-col">
        <div className="text-center mb-10">
          <h1 className="font-display font-black text-5xl md:text-6xl text-slate-900">{t("kiosk.welcome")}</h1>
          <p className="text-slate-500 mt-3 text-lg">{t("kiosk.choose_motif")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto w-full">
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`kiosk-category-${c.code}`}
              disabled={busy || isOffline}
              onClick={() => askPhone(c)}
              className="kiosk-card relative bg-white border-2 border-slate-200 rounded-2xl p-6 md:p-8 min-h-[200px] md:min-h-[220px] flex flex-col items-start gap-3 hover:shadow-lg active:scale-[0.97] disabled:opacity-50 select-none touch-manipulation"
              style={{ borderLeftWidth: 8, borderLeftColor: c.color }}
            >
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{c.code}</div>
              <div className="font-display font-bold text-3xl text-slate-900 leading-tight">{c.label}</div>
              <div className="mt-auto text-sm text-slate-500">
                {t("kiosk.priority")} {c.priority_level} • {t("kiosk.touch_to_take")}
              </div>
            </button>
          ))}
        </div>
      </main>

      {phoneStep && (
        <PhonePromptModal
          category={phoneStep.category}
          phone={phone}
          setPhone={setPhone}
          onSkip={() => submitTicket(false)}
          onConfirm={() => submitTicket(true)}
          onCancel={() => setPhoneStep(null)}
          busy={busy}
          t={t}
        />
      )}

      {issued && <TicketIssuedModal ticket={issued} agency={agency} onClose={() => setIssued(null)} onNew={() => setIssued(null)} t={t} />}
    </div>
  );
}

function KioskHeader({ agency, onSwitch, lang, setLang, t, now, isFullscreen, toggleFullscreen, online, backendReachable }) {
  const time = now || new Date();
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="flex items-center justify-between p-5">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="MAMA" className="w-12 h-12" />
          <div>
            <div className="font-display font-bold text-xl text-slate-900">MAMA</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{t("kiosk.welcome_clientele")}</div>
          </div>
        </div>
        {agency && (
          <div className="hidden md:flex items-center text-center px-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400">{t("kiosk.agency")}</div>
              <div className="font-display font-semibold text-lg text-slate-900">{agency.name}</div>
              <div className="text-[11px] text-slate-500">{agency.city} • {agency.opening_hours}</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <NetworkIndicator online={online} backendReachable={backendReachable} compact />
          <div className="hidden sm:block text-right">
            <div className="font-display font-bold text-2xl tabular-nums text-slate-900">
              {time.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <div className="text-[11px] text-slate-500 capitalize">
              {time.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
          <LangToggle lang={lang} setLang={setLang} />
          {toggleFullscreen && (
            <Button variant="outline" size="icon" onClick={toggleFullscreen}
                    data-testid="kiosk-fullscreen-toggle"
                    title="Plein écran">
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          )}
          {agency && onSwitch && (
            <Button variant="outline" size="sm" onClick={onSwitch}
                    data-testid="kiosk-switch-agency" className="border-slate-300">
              {t("kiosk.change_agency")}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="flex items-center gap-1 border border-slate-200 rounded-full px-1 py-1 bg-slate-50">
      <Globe className="w-3.5 h-3.5 text-slate-400 ml-1.5" />
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`lang-toggle-${l.code}`}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wider ${
            lang === l.code ? "bg-[#DC2626] text-white" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function PhonePromptModal({ category, phone, setPhone, onSkip, onConfirm, onCancel, busy, t }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="phone-prompt-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl">
        <div className="flex items-center justify-center mb-5">
          <div className="w-14 h-14 rounded-full bg-[#DC2626]/10 flex items-center justify-center">
            <Smartphone className="w-7 h-7 text-[#DC2626]" />
          </div>
        </div>
        <div className="text-center mb-2 font-mono font-bold text-xs uppercase tracking-widest" style={{ color: category.color }}>
          {category.code} • {category.label}
        </div>
        <h3 className="font-display font-bold text-2xl text-center text-slate-900">{t("kiosk.optional_phone")}</h3>
        <p className="text-center text-slate-500 text-sm mt-2 mb-6">{t("kiosk.phone_hint")}</p>
        <Input
          type="tel"
          inputMode="tel"
          placeholder="+261 34 12 345 67"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="kiosk-phone-input"
          className="h-14 text-center text-xl font-mono tracking-wider"
        />
        <div className="grid grid-cols-2 gap-3 mt-6">
          <Button variant="outline" onClick={onSkip} disabled={busy} data-testid="kiosk-phone-skip"
                  className="h-12 text-base">
            {t("kiosk.skip")}
          </Button>
          <Button onClick={onConfirm} disabled={busy || !phone.trim()}
                  data-testid="kiosk-phone-confirm"
                  className="h-12 text-base bg-[#DC2626] hover:bg-[#B91C1C]">
            {t("kiosk.confirm")}
          </Button>
        </div>
        <button onClick={onCancel} className="block mx-auto mt-4 text-xs text-slate-400 hover:text-slate-700">
          ← Annuler
        </button>
      </div>
    </div>
  );
}

function TicketIssuedModal({ ticket, agency, onClose, onNew, t }) {
  const printerMode = agency?.printer_mode || "browser";
  const [printing, setPrinting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const peopleAhead = ticket.people_ahead ?? 0;
  const printLabel = printerMode === "browser" ? t("ticket.print") : t("ticket.print_thermal");

  const onPrint = async () => {
    setPrinting(true);
    try {
      if (printerMode === "webusb" && isWebUsbSupported()) {
        const { data } = await api.get(`/tickets/${ticket.id}/escpos`);
        await printEscposBytes(data.bytes_b64);
        toast.success("Ticket imprimé");
      } else if (printerMode === "network") {
        await api.post(`/tickets/${ticket.id}/print-network`);
        toast.success("Envoyé à l'imprimante réseau");
      } else {
        // Browser-standard print via the dedicated layout (defaults to 80mm thermal)
        openTicketPrint(ticket, agency?.name, { widthMm: 80 });
      }
    } catch (e) {
      warnDev("print failed, fallback to browser print", e);
      toast.warning("Impression directe indisponible, ouverture du navigateur…");
      openTicketPrint(ticket, agency?.name, { widthMm: 80 });
    } finally {
      setPrinting(false);
    }
  };

  const onPrintWidth = (widthMm) =>
    openTicketPrint(ticket, agency?.name, { widthMm });

  const onCancelTicket = async () => {
    if (cancelling) return;
    if (!window.confirm("Annuler ce ticket ?")) return;
    setCancelling(true);
    try {
      await api.post(`/tickets/${ticket.id}/cancel-public`);
      toast.success("Ticket annulé");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annulation impossible");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ticket-issued-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-8 sm:p-10 shadow-2xl">
        <div id="print-area">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="MAMA" className="w-10 h-10" />
              <div>
                <div className="font-display font-bold text-slate-900">MAMA</div>
                <div className="text-xs text-slate-500">{ticket.agency_code}</div>
              </div>
            </div>
            <CheckCircle2 className="w-10 h-10 text-[#16A34A]" />
          </div>

          <div className="text-center my-6">
            <div className="text-sm uppercase tracking-[0.3em] text-slate-500 mb-2">{t("ticket.your_ticket")}</div>
            <div className="font-display font-black text-7xl md:text-8xl text-slate-900 tracking-tight" data-testid="issued-ticket-code">
              {ticket.full_ticket_code}
            </div>
            <div className="mt-3 inline-block px-4 py-1.5 rounded-full text-sm font-semibold"
                 style={{ background: `${ticket.color}1A`, color: ticket.color }}>
              {ticket.category_label}
            </div>
          </div>

          <div className="flex items-center justify-center mb-4">
            <img src={ticket.qr_image} alt="QR Code" className="w-32 h-32 border border-slate-200 rounded-lg" />
          </div>

          <div className="grid grid-cols-3 gap-3 text-center text-sm border-t border-slate-200 pt-4">
            <div>
              <Clock className="w-4 h-4 mx-auto mb-1 text-slate-400" />
              <div className="text-slate-500 text-[11px]">{t("ticket.issued_at")}</div>
              <div className="font-mono font-semibold">{new Date(ticket.created_at).toLocaleTimeString("fr-FR")}</div>
            </div>
            <div>
              <Users className="w-4 h-4 mx-auto mb-1 text-slate-400" />
              <div className="text-slate-500 text-[11px]">Personnes avant vous</div>
              <div className="font-display font-bold text-2xl text-[#DC2626] tabular-nums" data-testid="issued-people-ahead">
                {peopleAhead}
              </div>
            </div>
            <div>
              <Building2 className="w-4 h-4 mx-auto mb-1 text-slate-400" />
              <div className="text-slate-500 text-[11px]">{t("ticket.agency")}</div>
              <div className="font-semibold">{ticket.agency_code}</div>
            </div>
          </div>
          <div className="text-center text-xs text-slate-400 mt-4">{t("ticket.thanks")}</div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-6 print:hidden">
          <Button
            data-testid="ticket-print-button"
            onClick={onPrint}
            disabled={printing}
            className="h-14 bg-slate-900 hover:bg-slate-800 text-white text-base"
          >
            <Printer className="w-5 h-5 mr-2" />
            {printing ? "..." : printLabel}
          </Button>
          <Button
            data-testid="ticket-new-button"
            onClick={onNew}
            className="h-14 bg-[#16A34A] hover:bg-[#15803D] text-white text-base"
          >
            <RefreshCw className="w-5 h-5 mr-2" /> Nouveau ticket
          </Button>
          <Button
            data-testid="ticket-cancel-public-button"
            onClick={onCancelTicket}
            disabled={cancelling}
            variant="outline"
            className="h-14 text-base text-[#DC2626] border-[#DC2626]/40 hover:bg-red-50"
          >
            <X className="w-5 h-5 mr-2" /> {cancelling ? "..." : "Annuler"}
          </Button>
        </div>

        {/* Secondary print formats — for kiosks without a thermal printer */}
        <div className="flex items-center justify-center gap-3 mt-3 print:hidden">
          <button
            type="button"
            onClick={() => onPrintWidth(58)}
            data-testid="ticket-print-58mm"
            className="text-xs text-slate-500 hover:text-[#DC2626] underline-offset-2 hover:underline"
          >
            Format 58 mm
          </button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={() => onPrintWidth(80)}
            data-testid="ticket-print-80mm"
            className="text-xs text-slate-500 hover:text-[#DC2626] underline-offset-2 hover:underline"
          >
            Format 80 mm
          </button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={() => onPrintWidth(0)}
            data-testid="ticket-print-a4"
            className="text-xs text-slate-500 hover:text-[#DC2626] underline-offset-2 hover:underline"
          >
            Format A4
          </button>
        </div>
      </div>
    </div>
  );
}
